import React from "react";

import { BaseNodeType, LexContentType } from "@/types/common";
import { getImageUrl } from "@/utils/getImageUrl";
import CustomImage from "../shared/CustomImage";
import { Typography } from "../shared/Typography";
import VideoPlayer from "../shared/VideoPlayer";
import CollapsibleContainer from "./CollapsibleContainer";
import { SocialEmbed, YoutubeEmbed } from "./Embed";
import LiveUpdateComponent from "./LiveUpdateComponent";
import PdfDocComponent from "./PdfDocComponent";
import RelatedNewsComponent from "./RelatedNewsComponent";

type TextFormatType = 0 | 1 | 2 | 4 | 8 | 16 | 32 | 64;

interface HeadingNodeType extends BaseNodeType {
  type: "heading";
  tag: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
}

interface ParagraphNodeType extends BaseNodeType {
  type: "text";
  format?: string;
  indent?: number;
}

interface TextNodeType extends BaseNodeType {
  type: "text";
  text: string;
  format?: TextFormatType;
  style?: string;
}

interface AutolinkNodeType extends BaseNodeType {
  type: "autolink";
  url: string;
}

interface ListNodeType extends BaseNodeType {
  type: "list";
  tag: string;
  listType: string;
}

interface ListItemNodeType extends BaseNodeType {
  type: "listitem";
  checked?: boolean;
}

interface LinkNodeType extends BaseNodeType {
  type: "link";
  rel: string;
  target: string;
  url: string;
}

interface ImageNodeType extends BaseNodeType {
  type: "image";
  src: string;
  altText?: string;
  width: number;
  maxWidth: number;
  height: number;
  caption?: string;
  captionPosition: "top" | "bottom";
}

interface InlineImageNodeType extends BaseNodeType {
  type: "inline-image";
  src: string;
  altText?: string;
  width: number;
  maxWidth: number;
  height: number;
  position: "left" | "right";
  showCaption: boolean;
  caption?: string;
}

export interface CollapsibleContainerNodeType extends BaseNodeType {
  type: "collapsible-container";
  open: boolean;
}

interface LayoutContainerNodeType extends BaseNodeType {
  type: "layout-container";
  templateColumns: string;
}

interface VideoNodeType extends BaseNodeType {
  type: "video";
  src: string;
  hlsPlaybackUrl: string;
  alt: string;
  format: string;
}

interface TableNodeType extends BaseNodeType {
  type: "table";
  colWidths: string[];
}

interface TableRowNodeType extends BaseNodeType {
  type: "tablerow";
  height?: number;
}

interface TableCellNodeType extends BaseNodeType {
  type: "tablecell";
  backgroundColor: string | null;
  colSpan: number;
  headerState: number;
  rowSpan: number;
}

interface YoutubeNodeType extends BaseNodeType {
  type: "youtube";
  videoID: string;
  format: string;
  embedType: "video" | "playlist";
}

interface SocialEmbedNodeType extends BaseNodeType {
  type: "social-embed";
  format: string;
  url: string;
  embedType: string;
}

interface RelatedNewsNodeType extends BaseNodeType {
  type: "related-news";
  newsIds: string[];
}

interface PdfDocNodeType extends BaseNodeType {
  type: "pdf-doc";
  src: string;
  alt: string;
}

interface LiveUpdateNodeType extends BaseNodeType {
  type: "live-update";
  updates: {
    id: number;
    date: Date;
    content: LexContentType;
  }[];
}

type LexNodeType = BaseNodeType;

const isParagraphNode = (node: LexNodeType): node is ParagraphNodeType => {
  return node?.type === "paragraph";
};

const isTextNode = (node: LexNodeType): node is TextNodeType => {
  return node?.type === "text";
};

const isAutolinkNode = (node: LexNodeType): node is AutolinkNodeType => {
  return node?.type === "autolink";
};

const isHeadingNode = (node: LexNodeType): node is HeadingNodeType => {
  return node?.type === "heading";
};

const isListNode = (node: LexNodeType): node is ListNodeType => {
  return node?.type === "list";
};

const isListItemNode = (node: LexNodeType): node is ListItemNodeType => {
  return node?.type === "listitem";
};

const isLinkNode = (node: LexNodeType): node is LinkNodeType => {
  return node?.type === "link";
};

const isImageNode = (node: LexNodeType): node is ImageNodeType => {
  return node?.type === "image";
};

const isInlineImageNode = (node: LexNodeType): node is InlineImageNodeType => {
  return node?.type === "inline-image";
};

const isCollapsibleContainerNode = (node: LexNodeType): node is CollapsibleContainerNodeType => {
  return node?.type === "collapsible-container";
};

const isLayoutContainerNode = (node: LexNodeType): node is LayoutContainerNodeType => {
  return node?.type === "layout-container";
};

const isVideoNode = (node: LexNodeType): node is VideoNodeType => {
  return node?.type === "video";
};

const isTableNode = (node: LexNodeType): node is TableNodeType => {
  return node?.type === "table";
};

const isYoutubeNode = (node: LexNodeType): node is YoutubeNodeType => {
  return node?.type === "youtube";
};

const isSocialEmbedNode = (node: LexNodeType): node is SocialEmbedNodeType => {
  return node?.type === "social-embed";
};

const isRelatedNewsNode = (node: LexNodeType): node is RelatedNewsNodeType => {
  return node?.type === "related-news";
};

const isPdfDocNode = (node: LexNodeType): node is PdfDocNodeType => {
  return node?.type === "pdf-doc";
};

const isLiveUpdateNode = (node: LexNodeType): node is LiveUpdateNodeType => {
  return node?.type === "live-update";
};

// Helper function to parse style string into object
const parseStyleString = (styleString?: string | React.CSSProperties): React.CSSProperties => {
  if (!styleString) return {};

  if (styleString instanceof Object) {
    return styleString;
  }

  return styleString
    ?.split(";")
    ?.filter(style => style.trim())
    ?.reduce((acc: { [key: string]: string }, current) => {
      const [property, value] = current.split(":")?.map(str => str.trim());
      if (!property || !value) return acc;

      // Convert CSS property names from kebab-case to camelCase
      const camelProperty = property.replace(/-([a-z])/g, g => g[1]?.toUpperCase());
      acc[camelProperty] = value;
      return acc;
    }, {});
};

// Helper function to combine text formats
const formatText = (textContent: string, format?: TextFormatType): React.ReactNode => {
  if (!format) return textContent;

  let result = <React.Fragment>{textContent}</React.Fragment>;

  // Bold
  if (format & 1) result = <strong>{result}</strong>;
  // Italic
  if (format & 2) result = <em>{result}</em>;
  // Strikethrough
  if (format & 4) result = <del>{result}</del>;
  // Underline
  if (format & 8) result = <u>{result}</u>;
  // Code
  if (format & 16)
    result = <code className="bg-gray-200 dark:bg-[#555] px-[4px] rounded">{result}</code>;
  // Subscript
  if (format & 32) result = <sub>{result}</sub>;
  // Superscript
  if (format & 64) result = <sup>{result}</sup>;

  return result;
};

const getTextAlignment = (format?: string): React.CSSProperties => {
  switch (format) {
    case "left":
      return { textAlign: "left" };
    case "center":
      return { textAlign: "center" };
    case "right":
      return { textAlign: "right" };
    case "justify":
      return { textAlign: "justify" };
    default:
      return {};
  }
};

export const serializeToReact = (
  content: LexContentType,
  language: AppLocale = "te",
): React.ReactNode[] => {
  return [renderNode(content?.root, language)];
};

export const renderNode = (node: LexNodeType, language: AppLocale = "te"): React.ReactNode => {
  switch (node?.type) {
    case "root":
      return node?.children?.map((child, index) => (
        <React.Fragment key={index}>{renderNode(child, language)}</React.Fragment>
      ));

    case "paragraph": {
      if (!node?.children || node?.children?.length < 1) {
        return <br />;
      }

      if (!isParagraphNode(node)) return null;

      const paragraphContent = node?.children?.map((child, index) => (
        <React.Fragment key={index}>{renderNode(child, language)}</React.Fragment>
      ));

      // Combine text alignment with any existing styles
      const styles = {
        paddingLeft: `${(node?.indent || 0) * 1.5}em`,
        ...getTextAlignment(node?.format),
      };

      return (
        <Typography language={language} style={styles}>
          {paragraphContent}
        </Typography>
      );
    }

    case "text": {
      if (!isTextNode(node)) return null;
      const styles = parseStyleString(node?.style);
      const formattedText = formatText(node?.text || "", node?.format);
      return <span style={styles}>{formattedText}</span>;
    }

    case "autolink":
      if (!isAutolinkNode(node)) return null;
      const itemContent = node?.children?.map((child, index) => (
        <React.Fragment key={index}>{renderNode(child, language)}</React.Fragment>
      ));
      return <a href={node?.url || "#"}>{itemContent}</a>;

    case "heading": {
      if (!isHeadingNode(node)) return null;
      const headingContent = node?.children?.map((child, index) => (
        <React.Fragment key={index}>{renderNode(child, language)}</React.Fragment>
      ));
      return (
        <Typography language={language} variant={node?.tag}>
          {headingContent}
        </Typography>
      );
    }

    case "linebreak": {
      return (
        <span className="block">
          <br />
        </span>
      );
    }

    case "list": {
      if (!isListNode(node)) return null;
      const listContent = node?.children?.map((child, index) => (
        <React.Fragment key={index}>{renderNode(child, language)}</React.Fragment>
      ));
      if (node?.listType === "bullet") {
        return <ul className="list-disc pl-[16px]">{listContent}</ul>;
      } else if (node?.listType === "check") {
        return <ul className="pl-[16px]">{listContent}</ul>;
      } else {
        return <ol className="list-decimal pl-[16px]">{listContent}</ol>;
      }
    }

    case "listitem": {
      if (!isListItemNode(node)) return null;

      const itemContent = node?.children?.map((child, index) => (
        <React.Fragment key={index}>{renderNode(child, language)}</React.Fragment>
      ));

      if (node?.checked === true || node?.checked === false) {
        return (
          <li className="flex items-start gap-4">
            <input
              type="checkbox"
              checked={node?.checked || false}
              readOnly
              className="mt-[6px] w-[15px] h-[15px]"
            />
            <Typography language={language}>{itemContent}</Typography>
          </li>
        );
      }

      return (
        <li>
          <Typography language={language}>{itemContent}</Typography>
        </li>
      );
    }

    case "quote": {
      const itemContent = node?.children?.map((child, index) => (
        <React.Fragment key={index}>{renderNode(child, language)}</React.Fragment>
      ));

      return (
        <div className="bg-[#FFF6DF] dark:bg-[#665730] px-6 py-10 lg:px-8 lg:py-10 rounded-lg mt-8 relative">
          <Typography language={language} variant="body1" className="text-[#121212] ">
            {itemContent}
          </Typography>
          <div className="absolute flex items-center justify-center bg-red-600 rounded-full left-7 -top-7 h-14 w-14">
            {QuoteSvg()}
          </div>
        </div>
      );
    }

    case "link": {
      if (!isLinkNode(node)) return null;

      const itemContent = node?.children?.map((child, index) => (
        <React.Fragment key={index}>{renderNode(child, language)}</React.Fragment>
      ));

      return (
        <a
          href={node?.url || "#"}
          target={node?.target}
          rel={node?.rel}
          className="text-blue-500 underline"
          aria-label="Open link"
        >
          {itemContent}
        </a>
      );
    }

    case "image": {
      if (!isImageNode(node)) return null;

      const imageSrc = node?.src ? getImageUrl(node.src) : "";
      if (!imageSrc) return null;

      return (
        <span className="inline-block max-w-full " style={{ width: `${node?.width}px` }}>
          {node?.caption && node?.captionPosition === "top" && (
            <span className="block pl-4 mb-1 text-left text-[#858585] text-sm">
              {node?.caption}
            </span>
          )}

          <span
            className="relative block w-full"
            style={{ paddingBottom: `${((node?.height || 9) / (node?.width || 16)) * 100}%` }}
          >
            <span className="absolute inset-0">
              <CustomImage
                src={imageSrc}
                alt={node?.altText || ""}
                className="object-cover w-full h-full rounded-md"
              />
            </span>
          </span>

          {node?.caption && node?.captionPosition === "bottom" && (
            <span className="block pl-4 mt-1 text-left text-[#858585] text-sm">
              {node?.caption}
            </span>
          )}
        </span>
      );
    }

    case "inline-image": {
      if (!isInlineImageNode(node)) return null;

      const inlineImageSrc = node?.src ? getImageUrl(node.src) : "";
      if (!inlineImageSrc) return null;

      return (
        <span
          className="relative block overflow-hidden rounded-md"
          style={{
            width: `${node?.width}px`,
            maxWidth: "100%",
            float: node?.position,
            marginRight: node?.position === "left" ? "1rem" : "0rem",
            marginLeft: node?.position === "right" ? "1rem" : "0rem",
          }}
        >
          <span
            className="relative block w-full"
            style={{ paddingBottom: `${((node?.height || 9) / (node?.width || 16)) * 100}%` }}
          >
            <span className="absolute inset-0">
              <CustomImage
                src={inlineImageSrc}
                alt={node?.altText || ""}
                className="object-cover w-full h-full"
              />
            </span>
          </span>
          {node?.showCaption && node?.caption && (
            <span className="block pl-4 mt-1 text-left text-[#858585] text-sm">
              {node?.caption}
            </span>
          )}
        </span>
      );
    }

    case "horizontalrule":
      return <hr className="my-4 border-t border-t-borderColor dark:border-t-darkBorderColor " />;

    case "collapsible-container":
      if (!isCollapsibleContainerNode(node)) return null;
      return renderCollapsibleContainer(node);

    case "layout-container":
      if (!isLayoutContainerNode(node)) return null;
      return renderLayoutConatiner(node, language);

    case "video":
      if (!isVideoNode(node)) return null;
      return renderVideoNode(node);

    case "table":
      if (!isTableNode(node)) return null;
      return renderTableNode(node, language);

    case "youtube":
      if (!isYoutubeNode(node)) return null;
      return renderYoutubeNode(node);

    case "social-embed":
      if (!isSocialEmbedNode(node)) return null;
      return renderSocialEmbedNode(node);

    case "related-news":
      if (!isRelatedNewsNode(node)) return null;
      return renderRelatedNewsNode(node);

    case "pdf-doc":
      if (!isPdfDocNode(node)) return null;
      return renderPdfDocNode(node);

    case "live-update":
      if (!isLiveUpdateNode(node)) return null;
      return renderLiveUpdateNode(node);

    default:
      return null;
  }
};

const renderCollapsibleContainer = (node: CollapsibleContainerNodeType): React.ReactNode => {
  return (
    <div className="bg-[#f5f5f5] dark:bg-[#3a3d42] rounded">
      <CollapsibleContainer node={node} />
    </div>
  );
};

const renderLayoutConatiner = (
  node: LayoutContainerNodeType,
  language: AppLocale,
): React.ReactNode => {
  return (
    <div className="grid gap-5" style={{ gridTemplateColumns: node?.templateColumns }}>
      {node?.children?.map((child, index) => {
        const content = child.children?.map((child, index) => (
          <React.Fragment key={index}>{renderNode(child, language)}</React.Fragment>
        ));

        return <div key={index}>{content}</div>;
      })}
    </div>
  );
};

const renderVideoNode = (node: VideoNodeType): React.ReactNode => {
  return (
    <div className="relative w-full aspect-video">
      <VideoPlayer hlsPlaybackUrl={node?.hlsPlaybackUrl} />
    </div>
  );
};

const renderTableNode = (node: TableNodeType, language: AppLocale): React.ReactNode => {
  return (
    <div className="overflow-y-auto">
      <table>
        <tbody>
          {node?.children?.map((row, rowIndex) => {
            const rowData = row as TableRowNodeType;

            return (
              <tr key={rowIndex} style={{ height: rowData?.height || "auto" }}>
                {row?.children?.map((cell, cellIndex) => {
                  const cellData = cell as TableCellNodeType;
                  const childData = cell?.children?.map((child, index) => (
                    <React.Fragment key={index}>{renderNode(child, language)}</React.Fragment>
                  ));

                  const isHeader =
                    cellData?.headerState == 3 ||
                    cellData?.headerState === 2 ||
                    cellData?.headerState === 1;

                  return (
                    <td
                      key={cellIndex}
                      className={
                        "border border-borderColor dark:border-darkBorderColor px-2 py-[6px] " +
                        (isHeader && !cellData?.backgroundColor
                          ? " bg-[#f5f5f5] dark:bg-[#3A3D42] "
                          : " transparent ")
                      }
                      style={{
                        width: node?.colWidths[cellIndex],
                        minWidth: node?.colWidths[cellIndex],
                        backgroundColor: cellData?.backgroundColor || "unset",
                        fontWeight: isHeader ? "600" : "normal",
                      }}
                      colSpan={cellData?.colSpan}
                      rowSpan={cellData?.rowSpan}
                    >
                      {childData}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const renderYoutubeNode = (node: YoutubeNodeType): React.ReactNode => {
  return <YoutubeEmbed videoID={node?.videoID} embedType={node?.embedType} />;
};

const renderSocialEmbedNode = (node: SocialEmbedNodeType): React.ReactNode => {
  return <SocialEmbed url={node?.url} embedType={node?.embedType} format={node?.format} />;
};

const renderRelatedNewsNode = (node: RelatedNewsNodeType): React.ReactNode => {
  return <RelatedNewsComponent newsIds={node?.newsIds || []} />;
};

const renderPdfDocNode = (node: PdfDocNodeType): React.ReactNode => {
  return <PdfDocComponent src={node?.src} />;
};

const renderLiveUpdateNode = (node: LiveUpdateNodeType): React.ReactNode => {
  return <LiveUpdateComponent updates={node?.updates} />;
};

function QuoteSvg() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-8" viewBox="0 0 60 40" fill="none">
      <g clipPath="url(#clip0_609_302)">
        <path
          d="M47.6018 0.0310109C47.2352 1.15906 46.844 2.24378 46.5283 3.34861C45.5395 6.80857 45.4948 10.301 46.1694 13.8275C46.3203 14.6198 46.673 14.8488 47.457 14.8411C51.3568 14.8024 55.2566 14.8364 59.1563 14.8086C59.794 14.8039 60.0019 14.9943 60.0004 15.6349C59.985 23.4445 59.985 31.2558 60.0004 39.0654C60.0004 39.7649 59.7771 40.0016 59.067 40.0001C51.3445 39.9784 43.6219 39.9784 35.8993 39.997C35.197 39.9985 34.9644 39.7741 34.9675 39.0654C34.9906 32.6469 34.9275 26.2283 35.0045 19.8113C35.0569 15.5343 36.6109 11.7107 38.9782 8.20741C41.1514 4.99194 43.9161 2.36757 47.0858 0.159444C47.2059 0.0758852 47.3153 -0.060285 47.6033 0.0294635L47.6018 0.0310109Z"
          fill="white"
        />
        <path
          d="M25.0281 27.3703C25.0281 31.2387 25.0297 35.1072 25.0281 38.9757C25.0281 39.9474 24.9881 39.9846 24.0393 39.9846C16.3137 39.9846 8.58649 39.9799 0.860847 39.9954C0.26325 39.9954 -0.000124015 39.884 0.00295638 39.1954C0.03068 33.419 0.0322202 27.6426 0.0183584 21.8662C-0.00320441 14.465 3.04177 8.47201 8.32619 3.49561C9.45208 2.43565 10.6488 1.46389 11.8964 0.55557C12.1182 0.394642 12.3954 0.065048 12.6187 0.235261C12.9129 0.459632 12.6049 0.803153 12.5001 1.06776C11.3881 3.884 10.8413 6.80702 10.8999 9.83217C10.9276 11.248 11.1047 12.6546 11.3927 14.0488C11.5175 14.6507 11.7886 14.8349 12.3815 14.8318C16.2829 14.8101 20.1857 14.8411 24.0871 14.8024C24.8417 14.7946 25.0466 15.0283 25.042 15.7649C25.0112 19.6333 25.0281 23.5018 25.0281 27.3703Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_609_302">
          <rect width="60" height="40" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}
